import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    MessageCircle, Search, Plus, X, Copy, Edit2, Trash2, Settings, Tag, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const CommunicationsPage = () => {
    const { api, user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    
    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCategoriesModal, setShowCategoriesModal] = useState(false);
    const [editingMessage, setEditingMessage] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    
    // New message form
    const [newMessage, setNewMessage] = useState({
        title: '',
        message: '',
        category: ''
    });
    
    const [newCategory, setNewCategory] = useState('');
    
    const isAdmin = user?.role === 'chef' || user?.role === 'admin';
    
    // Fetch data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (categoryFilter && categoryFilter !== '__all__') params.append('category', categoryFilter);
            
            const [messagesRes, categoriesRes] = await Promise.all([
                api.get(`/communications?${params.toString()}`),
                api.get('/communications/categories')
            ]);
            
            setMessages(messagesRes.data || []);
            setCategories(categoriesRes.data || []);
        } catch (err) {
            console.error('Failed to fetch communications:', err);
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    }, [api, searchTerm, categoryFilter]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    
    // Copy message to clipboard
    const handleCopy = (msg) => {
        navigator.clipboard.writeText(msg.message);
        setCopiedId(msg.id);
        toast.success('Nachricht kopiert!');
        setTimeout(() => setCopiedId(null), 2000);
    };
    
    // Create message
    const handleCreate = async () => {
        if (!newMessage.title.trim()) {
            toast.error('Titel ist erforderlich');
            return;
        }
        if (!newMessage.message.trim()) {
            toast.error('Nachricht ist erforderlich');
            return;
        }
        
        try {
            await api.post('/communications', {
                ...newMessage,
                category: newMessage.category || null
            });
            toast.success('Nachricht erstellt!');
            setShowAddModal(false);
            setNewMessage({ title: '', message: '', category: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
        }
    };
    
    // Update message
    const handleUpdate = async () => {
        if (!editingMessage?.title?.trim()) {
            toast.error('Titel ist erforderlich');
            return;
        }
        
        try {
            await api.put(`/communications/${editingMessage.id}`, editingMessage);
            toast.success('Gespeichert!');
            setShowEditModal(false);
            setEditingMessage(null);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        }
    };
    
    // Delete message
    const handleDelete = async (id) => {
        if (!window.confirm('Nachricht wirklich löschen?')) return;
        
        try {
            await api.delete(`/communications/${id}`);
            toast.success('Gelöscht!');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
        }
    };
    
    // Create category
    const handleCreateCategory = async () => {
        if (!newCategory.trim()) {
            toast.error('Name ist erforderlich');
            return;
        }
        
        try {
            await api.post('/communications/categories', { name: newCategory.trim() });
            toast.success('Kategorie erstellt!');
            setNewCategory('');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };
    
    // Delete category
    const handleDeleteCategory = async (name) => {
        if (!window.confirm(`Kategorie "${name}" löschen?`)) return;
        
        try {
            await api.delete(`/communications/categories/${encodeURIComponent(name)}`);
            toast.success('Gelöscht!');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler');
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                            <MessageCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Kommunikation</h1>
                            <p className="text-sm text-muted-foreground">
                                WhatsApp-Vorlagen zum Kopieren
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {isAdmin && (
                            <>
                                <Button variant="outline" size="icon" onClick={() => setShowCategoriesModal(true)} title="Kategorien verwalten">
                                    <Settings className="w-4 h-4" />
                                </Button>
                                <Button onClick={() => setShowAddModal(true)} className="gap-2">
                                    <Plus className="w-4 h-4" />
                                    Nachricht erstellen
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                
                {/* Search & Filter */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger className="w-full sm:w-[200px]">
                            <SelectValue placeholder="Alle Kategorien" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">Alle Kategorien</SelectItem>
                            {categories.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                
                {/* Messages List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="text-center py-12">
                        <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                        <p className="text-muted-foreground">
                            {isAdmin ? 'Noch keine Vorlagen vorhanden. Erstelle die erste!' : 'Keine Vorlagen vorhanden.'}
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {messages.map(msg => (
                            <Card key={msg.id} className="group hover:shadow-md transition-shadow">
                                <CardHeader className="pb-2">
                                    <div className="flex items-start justify-between">
                                        <div className="space-y-1">
                                            <CardTitle className="text-lg">{msg.title}</CardTitle>
                                            {msg.category && (
                                                <Badge variant="secondary" className="text-xs">
                                                    <Tag className="w-3 h-3 mr-1" />
                                                    {msg.category}
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant={copiedId === msg.id ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => handleCopy(msg)}
                                                className="gap-1"
                                            >
                                                {copiedId === msg.id ? (
                                                    <>
                                                        <Check className="w-4 h-4" />
                                                        Kopiert!
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="w-4 h-4" />
                                                        Kopieren
                                                    </>
                                                )}
                                            </Button>
                                            {isAdmin && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        onClick={() => { setEditingMessage(msg); setShowEditModal(true); }}
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                                                        onClick={() => handleDelete(msg.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                                        {msg.message}
                                    </p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Add Modal */}
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Nachricht erstellen</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        <div>
                            <Label>Titel / Name *</Label>
                            <Input
                                value={newMessage.title}
                                onChange={(e) => setNewMessage({ ...newMessage, title: e.target.value })}
                                placeholder="z.B. Begrüßung Neukunde"
                            />
                        </div>
                        
                        <div>
                            <Label>Kategorie</Label>
                            <Select 
                                value={newMessage.category} 
                                onValueChange={(v) => setNewMessage({ ...newMessage, category: v === '__none__' ? '' : v })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Kategorie wählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Keine Kategorie</SelectItem>
                                    {categories.map(cat => (
                                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div>
                            <Label>Nachricht *</Label>
                            <Textarea
                                value={newMessage.message}
                                onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                                placeholder="Hallo! Vielen Dank für..."
                                rows={8}
                            />
                        </div>
                        
                        <div className="flex gap-2 pt-4">
                            <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                                Abbrechen
                            </Button>
                            <Button className="flex-1" onClick={handleCreate}>
                                Erstellen
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Nachricht bearbeiten</DialogTitle>
                    </DialogHeader>
                    
                    {editingMessage && (
                        <div className="space-y-4">
                            <div>
                                <Label>Titel / Name *</Label>
                                <Input
                                    value={editingMessage.title}
                                    onChange={(e) => setEditingMessage({ ...editingMessage, title: e.target.value })}
                                />
                            </div>
                            
                            <div>
                                <Label>Kategorie</Label>
                                <Select 
                                    value={editingMessage.category || '__none__'} 
                                    onValueChange={(v) => setEditingMessage({ ...editingMessage, category: v === '__none__' ? null : v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Kategorie wählen..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">Keine Kategorie</SelectItem>
                                        {categories.map(cat => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div>
                                <Label>Nachricht *</Label>
                                <Textarea
                                    value={editingMessage.message}
                                    onChange={(e) => setEditingMessage({ ...editingMessage, message: e.target.value })}
                                    rows={8}
                                />
                            </div>
                            
                            <div className="flex gap-2 pt-4">
                                <Button variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>
                                    Abbrechen
                                </Button>
                                <Button className="flex-1" onClick={handleUpdate}>
                                    Speichern
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            
            {/* Categories Modal */}
            <Dialog open={showCategoriesModal} onOpenChange={setShowCategoriesModal}>
                <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Tag className="w-5 h-5 text-primary" />
                            Kategorien verwalten
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Add new category */}
                        <div className="flex gap-2">
                            <Input
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                placeholder="Neue Kategorie..."
                                onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                            />
                            <Button onClick={handleCreateCategory}>
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                        
                        {/* List of categories */}
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {categories.length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                    Noch keine Kategorien vorhanden
                                </p>
                            ) : (
                                categories.map(cat => (
                                    <div
                                        key={cat}
                                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Tag className="w-4 h-4 text-muted-foreground" />
                                            <span className="font-medium">{cat}</span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-destructive hover:text-destructive"
                                            onClick={() => handleDeleteCategory(cat)}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                        
                        <Button variant="outline" className="w-full" onClick={() => setShowCategoriesModal(false)}>
                            Schließen
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CommunicationsPage;
